import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { X } from "@phosphor-icons/react";

// Shared PM edit form. Handles BOTH single-tenant `/pmr` (PUT, initial has no id)
// AND multi-PM CRUD (`/property-managers`, POST for new, PATCH for existing).
// Pass `multi={true}` to use the multi-PM endpoints; leave falsy for legacy single-row.
export default function PMRModal({ initial, onClose, onSaved, multi = false }) {
  const [form, setForm] = useState(initial || {
    name: "", email: "", phone: "", contact_name: "", role_title: "Maintenance Coordinator",
    logo_url: "", brand_color: "#00C4CC", default_cost_cap: 500,
    default_dispatch_window: "within 1.5 hours", custom_footer: "", contacts: [],
  });
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  const uploadLogo = (file) => {
    if (!file) return;
    if (file.size > 500_000) return toast.error("Logo must be under 500KB");
    const reader = new FileReader();
    reader.onload = () => setForm(f => ({ ...f, logo_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const addContact = () => setForm(f => ({
    ...f,
    contacts: [...(f.contacts || []), { name: "", role: "Maintenance Coordinator", email: "", phone: "" }],
  }));
  const updateContact = (idx, patch) => setForm(f => ({
    ...f,
    contacts: (f.contacts || []).map((c, i) => i === idx ? { ...c, ...patch } : c),
  }));
  const removeContact = (idx) => setForm(f => ({
    ...f,
    contacts: (f.contacts || []).filter((_, i) => i !== idx),
  }));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Company name + reply-to email required");
    setBusy(true);
    const payload = {
      name: form.name, email: form.email, phone: form.phone || "",
      contact_name: form.contact_name || "",
      role_title: form.role_title || "Maintenance Coordinator",
      logo_url: form.logo_url || "",
      brand_color: form.brand_color || "#00C4CC",
      default_cost_cap: Number(form.default_cost_cap || 500),
      default_dispatch_window: form.default_dispatch_window || "within 1.5 hours",
      custom_footer: form.custom_footer || "",
      contacts: (form.contacts || []).filter(c => (c.name || "").trim()),
    };
    try {
      if (multi) {
        if (initial?.id) await api.patch(`/property-managers/${initial.id}`, payload);
        else await api.post("/property-managers", payload);
      } else {
        await api.put("/pmr", payload);
      }
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4" data-testid="pmr-modal">
      <div className="bg-white max-w-lg w-full rounded-2xl border-2 border-[#0f172a] shadow-2xl overflow-hidden max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-[#0f172a] flex-shrink-0">
          <h2 className="font-display font-black text-xl uppercase">
            {initial?.id ? "Edit Property Manager" : (multi ? "New Property Manager" : "Property Manager")}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={20}/></button>
        </div>
        <div className="p-6 space-y-3 overflow-y-auto">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 pt-1">Company (the property management firm)</div>
          <Row label="Company Name (e.g. Property Management Redefined LLC)" v={form.name} tid="pmr-name-input" set={v => setForm({...form, name: v})}/>
          <Row label="Company Reply-To Email (landlord replies land here)" v={form.email} tid="pmr-email-input" set={v => setForm({...form, email: v})} type="email"/>
          <Row label="Company Phone" v={form.phone} tid="pmr-phone-input" set={v => setForm({...form, phone: v})}/>

          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 pt-3">Primary Maintenance Coordinator (shown in email signature)</div>
          <Row label="Coordinator Name (e.g. Myke Alferez)" v={form.contact_name} tid="pmr-contact-name-input" set={v => setForm({...form, contact_name: v})}/>
          <Row label="Role / Title" v={form.role_title} tid="pmr-role-title-input" set={v => setForm({...form, role_title: v})}/>

          <div className="pt-3 flex items-center justify-between">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Additional Coordinators (optional)</div>
            <button type="button" onClick={addContact} data-testid="pmr-add-contact-btn"
              className="h-7 px-2 border-2 border-[#0f172a] font-black uppercase tracking-widest text-[10px] hover:bg-slate-50 inline-flex items-center gap-1">
              + Add
            </button>
          </div>
          <div className="text-[10px] text-slate-500">Add Grace and any other coordinators at this company. They're used later for per-landlord contact selection.</div>
          {(form.contacts || []).length === 0 ? (
            <div className="text-[11px] text-slate-400 italic">No additional coordinators yet.</div>
          ) : (
            <div className="space-y-2">
              {(form.contacts || []).map((c, i) => (
                <div key={i} className="border-2 border-slate-200 p-2 relative" data-testid={`pmr-contact-row-${i}`}>
                  <button type="button" onClick={() => removeContact(i)}
                    className="absolute top-1 right-1 h-6 w-6 text-red-500 hover:bg-red-50 rounded flex items-center justify-center">
                    <X size={12}/>
                  </button>
                  <div className="grid grid-cols-2 gap-2 pr-6">
                    <input placeholder="Name" value={c.name} onChange={e => updateContact(i, { name: e.target.value })}
                      className="h-9 px-2 border border-slate-300 text-xs"/>
                    <input placeholder="Role" value={c.role} onChange={e => updateContact(i, { role: e.target.value })}
                      className="h-9 px-2 border border-slate-300 text-xs"/>
                    <input placeholder="Email" type="email" value={c.email} onChange={e => updateContact(i, { email: e.target.value })}
                      className="h-9 px-2 border border-slate-300 text-xs"/>
                    <input placeholder="Phone" value={c.phone} onChange={e => updateContact(i, { phone: e.target.value })}
                      className="h-9 px-2 border border-slate-300 text-xs"/>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 pt-3">Branding (white-label emails)</div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Logo</label>
            <div className="mt-1 flex items-center gap-3">
              {form.logo_url ? (
                <img src={form.logo_url} alt="Logo preview" className="h-14 max-w-[160px] object-contain border border-slate-300 bg-white p-1"/>
              ) : (
                <div className="h-14 w-[160px] border-2 border-dashed border-slate-300 flex items-center justify-center text-[10px] text-slate-400 font-black uppercase tracking-widest">No logo</div>
              )}
              <div className="flex flex-col gap-1">
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
                  onChange={e => uploadLogo(e.target.files?.[0])} data-testid="pmr-logo-input"/>
                <button type="button" onClick={() => fileInputRef.current?.click()}
                  className="h-9 px-3 border-2 border-[#0f172a] font-black uppercase tracking-widest text-[11px] hover:bg-slate-50">
                  Upload PNG/JPG
                </button>
                {form.logo_url && (
                  <button type="button" onClick={() => setForm({...form, logo_url: ""})}
                    className="h-8 px-2 border border-red-500 text-red-600 font-black uppercase tracking-widest text-[10px] hover:bg-red-50">
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Brand color</label>
              <div className="mt-1 flex items-center gap-2">
                <input type="color" value={form.brand_color || "#00C4CC"} onChange={e => setForm({...form, brand_color: e.target.value})}
                  data-testid="pmr-brand-color-input" className="h-11 w-14 border-2 border-[#0f172a] cursor-pointer p-0"/>
                <input type="text" value={form.brand_color || ""} onChange={e => setForm({...form, brand_color: e.target.value})}
                  className="flex-1 h-11 px-3 border-2 border-[#0f172a] text-sm font-mono"/>
              </div>
            </div>
            <Row label="Auto-approval cap ($)" v={form.default_cost_cap} tid="pmr-cost-cap-input" set={v => setForm({...form, default_cost_cap: v})} type="number"/>
          </div>
          <Row label="Dispatch window phrasing (shown in email)" v={form.default_dispatch_window} tid="pmr-dispatch-window-input" set={v => setForm({...form, default_dispatch_window: v})}/>
        </div>
        <div className="flex-shrink-0 border-t-2 border-slate-200 p-4 flex gap-2 bg-white">
          <button onClick={onClose} className="flex-1 h-11 border-2 border-[#0f172a] font-black uppercase tracking-widest text-xs">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="pmr-save-btn"
            className="flex-1 h-11 bg-[#00C4CC] text-white font-black uppercase tracking-widest text-xs disabled:opacity-50">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, v, set, type = "text", tid }) {
  return (
    <div>
      <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</label>
      <input value={v ?? ""} onChange={e => set(e.target.value)} type={type} data-testid={tid}
        className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] text-sm"/>
    </div>
  );
}
